import { TYPE_TEAMS } from '@/constants';
import { getRandomInt, getRandomItemFromArray, uncapitalizeFirstLetter } from '@/utilities';
import { v4 as uuidv4 } from 'uuid';
import Moving from '@/mixins/Moving';

import DrinkCoffee from '@/models/Activity/DrinkCoffee';
import IdleState from '@/models/Activity/IdleState';
import PlaceTask from '@/models/Activity/PlaceTask';
import ProcessTask from '@/models/Activity/ProcessTask';
import TakeTask from '@/models/Activity/TakeTask';
import Meeting from '@/models/Activity/Meeting';

export default class Employee {
  constructor({ team, index, productivity = 1 }) {
    this.id = uuidv4();
    this.index = index;
    this.team = team;
    this.productivityPerTick = productivity;
    this.task = undefined;
    this.skills = {};
    this.onSeat = true;

    this.currentCoord = team.getCoordInRoom(index);

    this.konvaNode = undefined;
    this.activity = new IdleState({ reference: this });
    this.fatigueValue = 0;

    this.addSkill(new Moving(this));
  }

  get inMeeting() {
    return this.activity.constructor.name === 'Meeting';
  }

  get isIdle() {
    return this.activity.constructor.name === 'IdleState';
  }

  get hasArrived() {
    return this.step >= this.path?.length - 1;
  }

  get path() {
    return this.skills.moving.path;
  }

  get step() {
    return this.skills.moving.step;
  }

  addSkill(skillInstance) {
    this.skills[uncapitalizeFirstLetter(skillInstance.constructor.name)] = skillInstance;
  }

  setKonvaNode(node) {
    this.konvaNode = node;
  }

  increaseFatigueLevel() {
    this.fatigueValue += 1;
  }

  decreaseFatigueLevel() {
    if (this.fatigueValue > 0) {
      this.fatigueValue -= getRandomInt(0, 1);
    }
  }

  setOnSeat() {
    this.onSeat = true;
    this.currentCoord = this.team.getCoordInRoom(this.index);
  }

  processByTick() {
    if (!this.onSeat && this.hasArrived) {
      this.activity.execute();
      return;
    }

    if (!this.onSeat) {
      this.skills.moving.move();
      return;
    }

    const randomActivity = getRandomItemFromArray(['idle', 'coffee', 'task']);

    if (this.fatigueValue >= 10 || randomActivity === 'coffee') {
      this.drinkCoffee();
      return;
    }

    if (randomActivity === 'idle') {
      this.beIdle();
      return;
    }

    if (!this.task && this.team.hasPendingTask) {
      this.takeTask();
      return;
    }

    if (this.task) {
      this.processTask();
    }

    this.activity.execute();
  }

  setActivity(activity) {
    this.activity = activity;
  }

  beIdle() {
    this.activity = new IdleState({ reference: this });
  }

  takeTask() {
    this.onSeat = false;
    this.activity = new TakeTask({
      reference: this,
      departurePath: this.team.getPathToCorners(this.index).toTopRight.slice(),
      returnPath: this.team.getPathToCorners(this.index).toTopRight.slice().reverse(),
    });
  }

  placeTask() {
    this.onSeat = false;
    this.activity = new PlaceTask({
      reference: this,
      departurePath: this.team.getPathToCorners(this.index).toBottomLeft.slice(),
      returnPath: this.team.getPathToCorners(this.index).toBottomLeft.slice().reverse(),
    });
  }

  drinkCoffee() {
    const pantry = this.team.groupManager.findAvailableRoomByType(TYPE_TEAMS.PANTRY);
    if (pantry) {
      this.onSeat = false;
      const coordInPantry = pantry.bookSeat(this);

      this.activity = new DrinkCoffee({
        reference: this,
        departurePath: [
          ...this.team.getPathToCorners(this.index).toBottomRight.slice(),
          ...this.team.getMemberPathToOtherRoom(pantry).slice(),
          coordInPantry,
        ],
        returnPath: [
          coordInPantry,
          ...pantry.getMemberPathToOtherRoom(this.team).slice(),
          ...this.team.getPathToCorners(this.index).toTopLeft.slice().reverse(),
        ],
        originalActivity: this.activity,
        pantry,
      });
      return;
    }
    this.activity.idle();
  }

  holdMeeting() {
    const participant = this.findMeetingParticipant();
    if (!participant) {
      this.task.reduceEfficiency();
      this.processTask();
      return;
    }
    const meetingRoom = this.team.groupManager.findAvailableRoomByType(TYPE_TEAMS.MEETING);
    let intersectionNode;

    this.task.hadMeeting = true;
    if (!meetingRoom && participant.onSeat && !participant.isIdle) {
      // 要改善 participant 不應該設 onSeat 為 false
      this.onSeat = false;
      participant.onSeat = false;
      const pathInfo = this.getMeetingPath(this, participant.team, participant.currentCoord);
      this.activity = new Meeting({
        reference: this,
        departurePath: pathInfo.departure,
        returnPath: pathInfo.return,
        originalActivity: this.activity,
        participant,
      });
      participant.setActivity(new Meeting({
        reference: participant,
        departurePath: [participant.currentCoord],
        returnPath: [participant.currentCoord],
        originalActivity: participant.activity,
        participant: this,
      }));
      return;
    }

    if (!meetingRoom) {
      const path = this.team.groupManager.pathManager.getGridPath(
        this.team.memberExitCoord, participant.team.memberExitCoord,
      );
      intersectionNode = path[Math.round((path.length - 1) / 2)];
    }
    this.setMeeting(participant, meetingRoom, intersectionNode);
    participant.setMeeting(this, meetingRoom, intersectionNode);
  }

  setMeeting(participant, meetingRoom, intersectionNode) {
    let pathInfo;
    if (meetingRoom) {
      pathInfo = this.getMeetingPath(this, meetingRoom, meetingRoom.bookSeat(this));
    } else {
      pathInfo = this.getMeetingOutsidePath(intersectionNode);
    }
    const departurePath = pathInfo.departure;
    const returnPath = pathInfo.return;

    this.onSeat = false;
    this.activity = new Meeting({
      reference: this,
      departurePath,
      returnPath,
      originalActivity: this.activity,
      meetingRoom,
      participant,
    });
  }

  findMeetingParticipant() {
    let participant = this.task.relatedEmployees.find((employee) => !employee.inMeeting);
    if (!participant) {
      const team = this.team.groupManager.findTaskNextTeam(this.team.type);
      participant = team?.findRandomIdleMember();
    }
    return participant;
  }

  // eslint-disable-next-line class-methods-use-this
  getMeetingPath(employee, meetingRoom, coordInRoom) {
    return {
      departure: [
        ...employee.team.getPathToCorners(employee.index).toBottomRight.slice(),
        ...employee.team.getMemberPathToOtherRoom(meetingRoom).slice(),
        coordInRoom,
      ],
      return: [
        coordInRoom,
        ...meetingRoom.getMemberPathToOtherRoom(employee.team).slice(),
        ...employee.team.getPathToCorners(employee.index).toTopLeft.slice().reverse(),
      ],
    };
  }

  getMeetingOutsidePath(intersectionNode) {
    const path = this.team.groupManager.pathManager.getGridPath(
      this.team.memberExitCoord, intersectionNode,
    );
    const departure = [
      ...this.team.getPathToCorners(this.index).toBottomRight.slice(),
      ...path,
    ];
    return {
      departure,
      return: departure.slice().reverse(),
    };
  }

  processTask() {
    this.activity = new ProcessTask({ reference: this });
  }

  deliverTask() {
    const subTasks = this.task.generateSubTasks();
    // 1. Team 要對 Task 某種監控
    // 2. Task 要有辦法通知 Team
    this.team.addTaskToDeliverList(subTasks);
    this.emptyTask();
  }

  emptyTask() {
    this.task = undefined;
    this.activity = new IdleState({ reference: this });
  }
}
